import { useState, useCallback } from 'react'
import type { CSSProperties } from 'react'
import { TopBar } from '../components/ui/TopBar'
import { ScreenWrapper } from '../components/ui/ScreenWrapper'
import { LocationAutocomplete } from '../components/rides/LocationAutocomplete'
import { RideCard } from '../components/rides/RideCard'
import { COLORS, FONT, SPACING, RADIUS } from '../components/ui/tokens'
import { useRidePublish } from '../hooks/useRides'
import { useGPS } from '../hooks/useGPS'
import type { Coords } from '../types/geo'
import type { CreateRidePayload } from '../types/ride'

function defaultDeparts(): string {
  const d = new Date(Date.now() + 3_600_000)
  d.setSeconds(0, 0)
  return d.toISOString().slice(0, 16)
}

export function PublishScreen() {
  const gps = useGPS()
  const { publish, publishing, error, published, reset } = useRidePublish()

  const [originName, setOriginName] = useState('')
  const [originCoords, setOriginCoords] = useState<Coords | null>(null)
  const [destName, setDestName] = useState('')
  const [destCoords, setDestCoords] = useState<Coords | null>(null)
  const [departsAt, setDepartsAt] = useState(defaultDeparts())
  const [seats, setSeats] = useState(1)
  const [validErr, setValidErr] = useState<string | null>(null)

  const handleOrigin = useCallback((name: string, coords: Coords | null) => { setOriginName(name); setOriginCoords(coords); setValidErr(null) }, [])
  const handleDest = useCallback((name: string, coords: Coords | null) => { setDestName(name); setDestCoords(coords); setValidErr(null) }, [])

  const handlePublish = useCallback(async () => {
    if (!originName.trim()) { setValidErr('Укажите точку отправления'); return }
    if (!destName.trim()) { setValidErr('Укажите точку назначения'); return }
    if (!departsAt) { setValidErr('Укажите время отправления'); return }
    const payload: CreateRidePayload = {
      origin: originCoords ?? { lat: 0, lng: 0 }, dest: destCoords ?? { lat: 0, lng: 0 },
      originName: originName.trim(), destName: destName.trim(),
      departsAt: new Date(departsAt).toISOString(), seatsTotal: seats,
    }
    const ride = await publish(payload)
    if (ride) {
      setOriginName(''); setOriginCoords(null)
      setDestName(''); setDestCoords(null)
      setDepartsAt(defaultDeparts())
      setSeats(1)
    }
  }, [originName, originCoords, destName, destCoords, departsAt, seats, publish])

  const inputStyle: CSSProperties = {
    width: '100%', padding: '11px 13px', backgroundColor: COLORS.bgElevated, border: `1px solid ${COLORS.border}`,
    borderRadius: RADIUS.md, color: COLORS.textPrimary, fontSize: FONT.base, outline: 'none', boxSizing: 'border-box', colorScheme: 'dark',
  }

  if (published) {
    return (
      <>
        <TopBar title="Поездка опубликована" />
        <ScreenWrapper>
          <div style={{ padding: SPACING.md }}>
            <div style={{
              backgroundColor: COLORS.success + '18', border: `1px solid ${COLORS.success}`, borderRadius: RADIUS.lg,
              padding: SPACING.md, marginBottom: SPACING.md, display: 'flex', gap: SPACING.sm, alignItems: 'center',
            }}>
              <span style={{ fontSize: 24 }}>✅</span>
              <div>
                <div style={{ color: COLORS.success, fontWeight: 600, fontSize: FONT.base }}>Поездка создана</div>
                <div style={{ color: COLORS.textSecond, fontSize: FONT.xs, marginTop: 2 }}>Пассажиры увидят её в поиске</div>
              </div>
            </div>
            <RideCard ride={published} showCancel={false} />
            <button onClick={reset} style={{
              width: '100%', padding: '13px', backgroundColor: COLORS.accent, color: '#fff', border: 'none',
              borderRadius: RADIUS.md, fontSize: FONT.base, fontWeight: 600, cursor: 'pointer', marginTop: SPACING.sm,
            }}>Опубликовать ещё одну</button>
          </div>
        </ScreenWrapper>
      </>
    )
  }

  return (
    <>
      <TopBar title="Поездка" subtitle="Опубликовать маршрут" />
      <ScreenWrapper>
        <div style={{ padding: SPACING.md, display: 'flex', flexDirection: 'column', gap: SPACING.md }}>
          <LocationAutocomplete label="Откуда" placeholder="Начальная точка" value={originName} onChange={handleOrigin} gpsCoords={gps.position} />
          <LocationAutocomplete label="Куда" placeholder="Конечная точка" value={destName} onChange={handleDest} gpsCoords={null} />

          <div>
            <label style={{ fontSize: FONT.xs, color: COLORS.textSecond, display: 'block', marginBottom: 4 }}>Дата и время отправления</label>
            <input style={inputStyle} type="datetime-local" value={departsAt} min={new Date().toISOString().slice(0, 16)} onChange={(e) => setDepartsAt(e.target.value)} />
          </div>

          <div>
            <label style={{ fontSize: FONT.xs, color: COLORS.textSecond, display: 'block', marginBottom: 8 }}>Свободных мест</label>
            <div style={{ display: 'flex', gap: SPACING.xs }}>
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <button key={n} onClick={() => setSeats(n)} style={{
                  flex: 1, height: 44, borderRadius: RADIUS.md, border: `1px solid ${seats === n ? COLORS.accent : COLORS.border}`,
                  backgroundColor: seats === n ? COLORS.accent + '22' : COLORS.bgElevated, color: seats === n ? COLORS.accent : COLORS.textPrimary,
                  fontSize: FONT.md, fontWeight: seats === n ? 700 : 400, cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                }}>{n}</button>
              ))}
            </div>
          </div>

          <div style={{ backgroundColor: COLORS.bgElevated, borderRadius: RADIUS.md, padding: SPACING.sm, fontSize: FONT.xs }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
              <span style={{ color: COLORS.textSecond }}>Откуда</span>
              <span style={{ color: originCoords ? COLORS.success : COLORS.textDisabled }}>
                {originCoords ? `${originCoords.lat.toFixed(4)}, ${originCoords.lng.toFixed(4)}` : 'не выбрано'}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: COLORS.textSecond }}>Куда</span>
              <span style={{ color: destCoords ? COLORS.success : COLORS.textDisabled }}>
                {destCoords ? `${destCoords.lat.toFixed(4)}, ${destCoords.lng.toFixed(4)}` : 'не выбрано'}
              </span>
            </div>
          </div>

          {(validErr ?? error) && <p style={{ color: COLORS.error, fontSize: FONT.sm, margin: 0 }}>{validErr ?? error}</p>}

          <button onClick={() => void handlePublish()} disabled={publishing} style={{
            padding: '14px', fontSize: FONT.base, fontWeight: 600, backgroundColor: publishing ? COLORS.bgElevated : COLORS.accent,
            color: publishing ? COLORS.textDisabled : '#fff', border: 'none', borderRadius: RADIUS.md, cursor: publishing ? 'default' : 'pointer',
          }}>{publishing ? 'Публикация...' : 'Опубликовать поездку'}</button>

          {!gps.position && <p style={{ color: COLORS.textDisabled, fontSize: FONT.xs, textAlign: 'center', margin: 0 }}>Включите GPS, чтобы использовать текущее местоположение</p>}
        </div>
      </ScreenWrapper>
    </>
  )
}
